import type { MapStore } from '../builder/MapStore';
import {
  addComponent,
  compileMap,
  compilePortals,
  createMap,
  mapProblems,
  mapWarnings,
  moveComponent,
  rectOf,
  removeComponent,
  rotateComponent,
  slugify,
  type MapDocument,
  type Rotation,
} from '../builder/MapDocument';
import {
  CELL_SIZE,
  COMPONENT_KINDS,
  componentSpec,
  footprint,
  type ComponentKind,
} from '../builder/components';
import { DIFFICULTY_TIERS, type DifficultyTier } from '../config/difficulty';
import { button, el } from './dom';

type EditMode = 'stamp' | 'erase' | 'rotate' | 'move' | 'pan';

export interface MapEditorCallbacks {
  /** Save the map and start a run on the compiled level. */
  onTest: (map: MapDocument) => void;
  /** Leave the builder, back to the main menu. */
  onExit: () => void;
  /** Open the 3D room builder on this map (added in the build-mode pass). */
  onEnterBuild?: (map: MapDocument) => void;
}

/** The buildable area, in cells. */
export const MAP_GRID_COLS = 64;
export const MAP_GRID_ROWS = 40;
export const MAP_CANVAS_WIDTH = 640;
export const MAP_CANVAS_HEIGHT = 420;
/** Zoom bounds, pixels per cell. */
const MIN_ZOOM = 6;
const MAX_ZOOM = 32;
/** How many edits the undo button can walk back. */
const HISTORY_LIMIT = 60;
/** A cell line every this many cells is drawn heavier, as a ruler. */
const MAJOR_GRID = 8;

/**
 * The 2D map builder.
 *
 * A grid of prebuilt components: stamp a room or a hallway, rotate it, move it,
 * erase it. Doorways are not authored — they are generated wherever two
 * components touch, which is what the highlight on a shared edge shows — and
 * "Test run" compiles the layout into an ordinary level and reloads into it.
 */
export class MapEditor {
  readonly root = el('div', 'map-editor hidden');

  private readonly canvas = el('canvas', 'map-canvas');
  private readonly title = el('h1', 'map-title', 'MAP BUILDER');
  private readonly problems = el('div', 'map-problems');
  private readonly draftSelect = el('select', 'map-draft-select');
  private map: MapDocument = createMap();
  private mode: EditMode = 'stamp';
  private kind: ComponentKind = 'room';
  /** The quarter turn the next stamped component gets. */
  private stampRotation: Rotation = 0;
  private hoverCell: [number, number] | null = null;
  private selectedId: string | null = null;
  /** Pixels per cell and the cell coordinate at the canvas' top-left corner. */
  private scale = 10;
  private viewX = 0;
  private viewZ = 0;
  /** The pointer dragging the view, when panning. */
  private panPointer: number | null = null;
  private panLastX = 0;
  private panLastY = 0;
  /** True while the space bar is held, which turns a drag into a pan. */
  private spaceHeld = false;
  private readonly nameField = el('input', 'map-name');
  private readonly seedField = el('input', 'map-seed');
  private readonly zoomLabel = el('span', 'map-zoom-label', '100%');
  private readonly stampLabel = el('span', 'map-stamp-label', 'Stamp 0°');
  private difficultyField: HTMLSelectElement | null = null;
  /** Snapshots of the map, newest last, so an edit can be taken back. */
  private readonly history: string[] = [];
  private readonly undoButton = el('button', 'map-action map-undo', 'Undo');

  /** The 2D drawing context, looked up once: a headless canvas has none. */
  private readonly context: CanvasRenderingContext2D | null;

  constructor(
    parent: HTMLElement,
    private readonly store: MapStore,
    private readonly callbacks: MapEditorCallbacks,
  ) {
    this.canvas.width = MAP_CANVAS_WIDTH;
    this.canvas.height = MAP_CANVAS_HEIGHT;
    this.context = this.canvas.getContext('2d');
    this.nameField.type = 'text';
    this.seedField.type = 'number';

    this.nameField.addEventListener('input', () => {
      this.map.name = this.nameField.value;
      this.refresh();
    });
    this.seedField.addEventListener('input', () => {
      const value = Number(this.seedField.value);
      if (Number.isFinite(value)) this.map.seed = value;
      this.refresh();
    });

    this.canvas.addEventListener('pointermove', (event) => {
      if (this.panPointer !== null && event.pointerId === this.panPointer) {
        this.panBy(event.clientX - this.panLastX, event.clientY - this.panLastY);
        this.panLastX = event.clientX;
        this.panLastY = event.clientY;
        return;
      }
      this.hoverCell = this.cellAt(event);
      this.draw();
    });
    this.canvas.addEventListener('pointerleave', () => {
      this.hoverCell = null;
      this.draw();
    });
    this.canvas.addEventListener('pointerup', (event) => {
      if (event.pointerId === this.panPointer) this.panPointer = null;
    });
    this.canvas.addEventListener('pointerdown', (event) => {
      // The editor takes focus on a click so the keyboard's undo works without
      // a global listener following the page around.
      this.root.focus();
      // A middle/right drag, or a drag with space held, moves the view instead
      // of editing: the board is bigger than the canvas.
      if (this.mode === 'pan' || event.button === 1 || event.button === 2 || this.spaceHeld) {
        event.preventDefault();
        this.panPointer = event.pointerId;
        this.panLastX = event.clientX;
        this.panLastY = event.clientY;
        this.canvas.setPointerCapture?.(event.pointerId);
        return;
      }
      this.onCanvasClick(event);
    });
    this.canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    this.canvas.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        this.zoomAtPointer(event);
      },
      { passive: false },
    );

    this.undoButton.type = 'button';
    this.undoButton.addEventListener('click', () => this.undo());
    this.root.tabIndex = -1;
    this.root.addEventListener('keydown', (event) => {
      // A field keeps its own undo: Ctrl+Z while typing the name should edit the
      // name, not the layout behind it.
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        this.undo();
        return;
      }
      if (event.key === ' ') {
        // Space is the pan modifier here; it must not also scroll the overlay.
        event.preventDefault();
        this.spaceHeld = true;
        return;
      }
      if (event.key === 'r' || event.key === 'R') {
        event.preventDefault();
        this.rotateStamp();
        return;
      }
      const step = event.shiftKey ? 1 : 4;
      if (event.key === 'ArrowLeft') this.panByCells(-step, 0);
      else if (event.key === 'ArrowRight') this.panByCells(step, 0);
      else if (event.key === 'ArrowUp') this.panByCells(0, -step);
      else if (event.key === 'ArrowDown') this.panByCells(0, step);
      else if (event.key === '+' || event.key === '=') this.zoomAtCanvasCentre(1.2);
      else if (event.key === '-') this.zoomAtCanvasCentre(1 / 1.2);
      else return;
      event.preventDefault();
    });
    this.root.addEventListener('keyup', (event) => {
      if (event.key === ' ') this.spaceHeld = false;
    });

    parent.append(this.root);
    this.build();
  }

  get visible(): boolean {
    return !this.root.classList.contains('hidden');
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle('hidden', !visible);
    if (visible) this.refreshDrafts();
  }

  /** Show the editor on a map: a draft, or a fresh template. */
  open(map: MapDocument | null = null): void {
    this.map = map ?? createMap();
    this.selectedId = null;
    this.hoverCell = null;
    this.mode = 'stamp';
    this.kind = 'room';
    this.stampRotation = 0;
    this.fitView();
    // A freshly opened map starts a fresh undo history: New and Load save their
    // predecessors as drafts, so nothing is lost by not walking back into them.
    this.history.length = 0;
    this.setVisible(true);
    this.root.focus();
    this.refresh();
  }

  /** The map being edited, for the 3D builder. */
  get document(): MapDocument {
    return this.map;
  }

  // ------------------------------------------------------------------ layout

  private build(): void {
    const difficulty = el('select', 'map-difficulty');
    for (const tier of DIFFICULTY_TIERS) {
      const option = el('option', undefined, tier.toUpperCase());
      option.value = tier;
      difficulty.append(option);
    }
    difficulty.addEventListener('change', () => {
      this.map.difficulty = difficulty.value as DifficultyTier;
      this.refresh();
    });
    this.difficultyField = difficulty;

    const header = el('div', 'map-header');
    header.append(
      this.title,
      this.field('NAME', this.nameField),
      this.field('DIFFICULTY', difficulty),
      this.field('SEED', this.seedField),
    );

    const palette = el('div', 'map-palette');
    for (const kind of COMPONENT_KINDS) {
      const spec = componentSpec(kind);
      const item = button(
        `${spec.label} ${spec.cells[0]}×${spec.cells[1]}`,
        () => {
          this.kind = kind;
          this.mode = 'stamp';
          this.refresh();
        },
        'map-palette-button',
      );
      item.dataset.kind = kind;
      palette.append(item);
    }
    palette.append(
      button('Erase', () => this.setMode('erase'), 'map-tool'),
      button('Rotate', () => this.setMode('rotate'), 'map-tool'),
      button('Move', () => this.setMode('move'), 'map-tool'),
      button('Pan', () => this.setMode('pan'), 'map-tool'),
    );

    const zoom = el('div', 'map-zoom');
    zoom.append(
      button('−', () => this.zoomAtCanvasCentre(1 / 1.25), 'map-zoom-button'),
      this.zoomLabel,
      button('+', () => this.zoomAtCanvasCentre(1.25), 'map-zoom-button'),
      button('Fit', () => this.fitView(() => this.refresh()), 'map-zoom-button'),
      this.stampLabel,
      button('Rotate stamp', () => this.rotateStamp(), 'map-zoom-button'),
    );

    const view = el('div', 'map-view');
    view.append(this.canvas, zoom);

    const board = el('div', 'map-board');
    board.append(view, palette);

    const drafts = el('div', 'map-row');
    this.draftSelect.className = 'map-draft-select';
    drafts.append(
      button('New', () => this.newMap(), 'map-action'),
      button('Save draft', () => this.saveDraft(), 'map-action'),
      this.draftSelect,
      button('Load', () => this.loadDraft(), 'map-action'),
      button('Delete', () => this.deleteDraft(), 'map-action'),
    );

    const files = el('div', 'map-row');
    const importInput = el('input', 'map-import');
    importInput.type = 'file';
    importInput.accept = 'application/json,.json';
    importInput.addEventListener('change', () => void this.importDraft(importInput));
    files.append(
      button('Test run', () => this.test(), 'map-action map-primary'),
      ...(this.callbacks.onEnterBuild
        ? [button('Edit in 3D', () => this.enterBuild(), 'map-action')]
        : []),
      button('Export level JSON', () => this.exportLevel(), 'map-action'),
      button('Export draft', () => this.exportDraft(), 'map-action'),
      button('Import draft', () => importInput.click(), 'map-action'),
      importInput,
    );

    const footer = el('div', 'map-row');
    footer.append(
      this.undoButton,
      button('Back', () => this.callbacks.onExit(), 'map-action'),
    );

    this.root.append(header, this.problems, board, drafts, files, footer);
  }

  private field(label: string, control: HTMLElement): HTMLElement {
    const wrapper = el('label', 'map-field');
    wrapper.append(el('span', 'map-field-label', label), control);
    return wrapper;
  }

  private setMode(mode: EditMode): void {
    this.mode = mode;
    this.selectedId = null;
    this.refresh();
  }

  // -------------------------------------------------------------------- undo

  /** Remember the map as it is now, so the next change can be taken back. */
  private snapshot(): void {
    this.history.push(JSON.stringify(this.map));
    if (this.history.length > HISTORY_LIMIT) this.history.shift();
  }

  /** Take back the last change. */
  private undo(): void {
    const previous = this.history.pop();
    if (previous === undefined) return;
    const restored = parseDraft(JSON.parse(previous));
    if (restored) this.map = restored;
    this.selectedId = null;
    this.hoverCell = null;
    this.refresh();
  }

  /** True while there is something to take back. */
  get canUndo(): boolean {
    return this.history.length > 0;
  }

  // ------------------------------------------------------------------ editing

  private onCanvasClick(event: PointerEvent): void {
    const cell = this.cellAt(event);
    if (!cell) return;

    // The snapshot is taken before the change, and dropped again if the action
    // turned out to change nothing: a refused stamp or an erase on empty grid
    // must not leave a no-op for Undo to walk back to.
    const before = JSON.stringify(this.map);
    this.snapshot();

    if (this.mode === 'stamp') {
      if (this.canPlace(this.kind, cell, this.stampRotation)) {
        addComponent(this.map, this.kind, cell, this.stampRotation);
      }
    } else if (this.mode === 'erase') {
      const component = this.componentInCell(cell);
      if (component) removeComponent(this.map, component.id);
    } else if (this.mode === 'rotate') {
      const component = this.componentInCell(cell);
      if (component) {
        const previous = {
          rotation: component.rotation,
          cells: [...component.cells] as [number, number],
        };
        rotateComponent(this.map, component.id);
        if (this.overlapsAnything(component.id) || !this.inBounds(component)) {
          // Put it back: the turned footprint does not fit where it is.
          component.rotation = previous.rotation;
          component.cells = previous.cells;
        }
      }
    } else {
      // Move: pick a component, then drop it on the next cell.
      const component = this.componentInCell(cell);
      if (component) {
        this.selectedId = component.id;
      } else if (this.selectedId) {
        const moving = this.map.components.find((entry) => entry.id === this.selectedId);
        if (moving) {
          const previous = [...moving.cell] as [number, number];
          moveComponent(this.map, moving.id, cell);
          if (this.overlapsAnything(moving.id) || !this.inBounds(moving)) {
            moveComponent(this.map, moving.id, previous);
          } else {
            this.selectedId = null;
          }
        }
      }
    }
    if (JSON.stringify(this.map) === before) this.history.pop();
    this.refresh();
  }

  private overlapsAnything(id: string): boolean {
    const component = this.map.components.find((entry) => entry.id === id);
    if (!component) return false;
    const rect = rectOf(component);
    return this.map.components.some(
      (other) =>
        other.id !== id &&
        rect.minX < rectOf(other).maxX &&
        rectOf(other).minX < rect.maxX &&
        rect.minZ < rectOf(other).maxZ &&
        rectOf(other).minZ < rect.maxZ,
    );
  }

  private canPlace(
    kind: ComponentKind,
    cell: [number, number],
    rotation: Rotation = this.stampRotation,
  ): boolean {
    const [w, d] = footprint(kind, rotation);
    if (cell[0] < 0 || cell[1] < 0) return false;
    if (cell[0] + w > MAP_GRID_COLS || cell[1] + d > MAP_GRID_ROWS) return false;
    const minX = cell[0] * CELL_SIZE;
    const minZ = cell[1] * CELL_SIZE;
    const maxX = minX + w * CELL_SIZE;
    const maxZ = minZ + d * CELL_SIZE;
    return !this.map.components.some((other) => {
      const rect = rectOf(other);
      return minX < rect.maxX && rect.minX < maxX && minZ < rect.maxZ && rect.minZ < maxZ;
    });
  }

  /** True when a component's footprint stays inside the buildable board. */
  private inBounds(component: MapDocument['components'][number]): boolean {
    const rect = rectOf(component);
    return (
      rect.minX >= 0 &&
      rect.minZ >= 0 &&
      rect.maxX <= MAP_GRID_COLS * CELL_SIZE &&
      rect.maxZ <= MAP_GRID_ROWS * CELL_SIZE
    );
  }

  private componentInCell(cell: [number, number]): MapDocument['components'][number] | undefined {
    const x = (cell[0] + 0.5) * CELL_SIZE;
    const z = (cell[1] + 0.5) * CELL_SIZE;
    return this.map.components.find((component) => {
      const rect = rectOf(component);
      return x >= rect.minX && x <= rect.maxX && z >= rect.minZ && z <= rect.maxZ;
    });
  }

  private cellAt(event: PointerEvent): [number, number] | null {
    const rect = this.canvas.getBoundingClientRect();
    // The canvas is drawn at its attribute size but displayed at whatever the
    // layout (and the UI-scale zoom) gives it, so the pointer is mapped back
    // into canvas pixels before the grid is consulted. The canvas keeps its
    // aspect ratio, so one scale covers both axes.
    const displayScale = rect.width > 0 ? rect.width / MAP_CANVAS_WIDTH : 1;
    const x = (event.clientX - rect.left) / displayScale;
    const z = (event.clientY - rect.top) / displayScale;
    if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
    return [Math.floor(x / this.scale + this.viewX), Math.floor(z / this.scale + this.viewZ)];
  }

  // -------------------------------------------------------------------- view

  /** Pixels per cell in the current view, for tests and the zoom readout. */
  get pixelsPerCell(): number {
    return this.scale;
  }

  /** The cell coordinate at the view's top-left corner, for tests. */
  get viewOrigin(): [number, number] {
    return [this.viewX, this.viewZ];
  }

  /** Fit the whole board in the canvas, centred. */
  private fitView(after?: () => void): void {
    this.scale = Math.min(MAP_CANVAS_WIDTH / MAP_GRID_COLS, MAP_CANVAS_HEIGHT / MAP_GRID_ROWS);
    this.viewX = -(MAP_CANVAS_WIDTH / this.scale - MAP_GRID_COLS) / 2;
    this.viewZ = -(MAP_CANVAS_HEIGHT / this.scale - MAP_GRID_ROWS) / 2;
    this.updateZoomLabel();
    this.draw();
    after?.();
  }

  /** Zoom about a point in canvas pixels, keeping the cell under it still. */
  private zoomAtCanvasPoint(px: number, pz: number, factor: number): void {
    const cellX = px / this.scale + this.viewX;
    const cellZ = pz / this.scale + this.viewZ;
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.scale * factor));
    if (Math.abs(next - this.scale) < 1e-6) return;
    this.scale = next;
    this.viewX = cellX - px / this.scale;
    this.viewZ = cellZ - pz / this.scale;
    this.clampView();
    this.updateZoomLabel();
    this.draw();
  }

  private zoomAtPointer(event: WheelEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const displayScale = rect.width > 0 ? rect.width / MAP_CANVAS_WIDTH : 1;
    this.zoomAtCanvasPoint(
      (event.clientX - rect.left) / displayScale,
      (event.clientY - rect.top) / displayScale,
      Math.exp(-event.deltaY * 0.0015),
    );
  }

  private zoomAtCanvasCentre(factor: number): void {
    this.zoomAtCanvasPoint(MAP_CANVAS_WIDTH / 2, MAP_CANVAS_HEIGHT / 2, factor);
  }

  /** Pan by a pointer delta in client pixels. */
  private panBy(dx: number, dz: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const displayScale = rect.width > 0 ? rect.width / MAP_CANVAS_WIDTH : 1;
    this.viewX -= dx / displayScale / this.scale;
    this.viewZ -= dz / displayScale / this.scale;
    this.clampView();
    this.updateZoomLabel();
    this.draw();
  }

  /** Pan by whole cells, as the arrow keys do. */
  private panByCells(dx: number, dz: number): void {
    this.viewX += dx;
    this.viewZ += dz;
    this.clampView();
    this.updateZoomLabel();
    this.draw();
  }

  /** Keep a strip of the board reachable when panning. */
  private clampView(): void {
    const margin = 4;
    this.viewX = Math.min(MAP_GRID_COLS - margin, Math.max(-margin, this.viewX));
    this.viewZ = Math.min(MAP_GRID_ROWS - margin, Math.max(-margin, this.viewZ));
  }

  private updateZoomLabel(): void {
    const fit = Math.min(MAP_CANVAS_WIDTH / MAP_GRID_COLS, MAP_CANVAS_HEIGHT / MAP_GRID_ROWS);
    const percent = Math.round((this.scale / fit) * 100);
    this.zoomLabel.textContent = `${percent}%`;
    this.stampLabel.textContent = `Stamp ${this.stampRotation}°`;
  }

  /** Turn the next stamped component a quarter turn. */
  private rotateStamp(): void {
    this.stampRotation = ((this.stampRotation + 90) % 360) as Rotation;
    this.updateZoomLabel();
    this.draw();
  }

  // ----------------------------------------------------------------- actions

  private test(): void {
    if (mapProblems(this.map).length > 0) return;
    this.callbacks.onTest(this.map);
  }

  private enterBuild(): void {
    if (mapProblems(this.map).length > 0) return;
    this.callbacks.onEnterBuild?.(this.map);
  }

  /**
   * Start a fresh layout.
   *
   * Anything already placed is saved as a draft first, so a stray click on
   * "New" can never throw away a layout the player was building.
   */
  private newMap(): void {
    if (this.map.components.length > 0) this.store.save(this.map);
    this.open(createMap());
  }

  private saveDraft(): void {
    const id = this.store.save(this.map);
    this.refreshDrafts(id);
  }

  private loadDraft(): void {
    const draft = this.store.load(this.draftSelect.value);
    if (draft) this.open(draft);
  }

  private deleteDraft(): void {
    this.store.remove(this.draftSelect.value);
    this.refreshDrafts();
  }

  private exportLevel(): void {
    const level = compileMap(this.map);
    download(`${level.id}.level.json`, JSON.stringify(level, null, 2));
  }

  private exportDraft(): void {
    download(`${slugify(this.map.name)}.map.json`, JSON.stringify(this.map, null, 2));
  }

  private async importDraft(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      // A draft is a MapDocument; an exported level is not, so only accept the
      // shape the editor can actually keep editing.
      const map = parseDraft(parsed);
      if (map) this.open(map);
      else
        this.setProblems([
          'That file is not a map draft (export a level to ship it, a draft to edit it).',
        ]);
    } catch {
      this.setProblems(['That file could not be read as JSON.']);
    }
    input.value = '';
  }

  // ---------------------------------------------------------------- rendering

  private refresh(): void {
    this.nameField.value = this.map.name;
    this.seedField.value = String(this.map.seed);
    if (this.difficultyField) this.difficultyField.value = this.map.difficulty;
    this.title.textContent = `MAP BUILDER — ${slugify(this.map.name)}`;
    this.setProblems(mapProblems(this.map), mapWarnings(this.map));
    this.undoButton.disabled = this.history.length === 0;
    this.undoButton.classList.toggle('disabled', this.undoButton.disabled);
    this.refreshDrafts();
    this.updateZoomLabel();
    this.draw();
  }

  /**
   * Show the map's problems and its warnings.
   *
   * Problems stop a test run (an overlap, a missing component); warnings only say
   * a deck will play badly, because a walled-off doorway can be a design choice
   * and refusing to test a map over one would be worse than telling the player.
   */
  private setProblems(problems: string[], warnings: string[] = []): void {
    this.problems.replaceChildren(
      ...problems.map((problem) => el('div', 'map-problem', problem)),
      ...warnings.map((warning) => el('div', 'map-warning', warning)),
    );
    this.problems.classList.toggle('hidden', problems.length + warnings.length === 0);
  }

  private refreshDrafts(select?: string): void {
    // Keep whatever the player had picked: this runs after every edit, and
    // rebuilding the list must not move the selection under their cursor.
    const previous = select ?? this.draftSelect.value;
    const drafts = this.store.list();
    this.draftSelect.replaceChildren(
      ...drafts.map((draft) => {
        const option = el('option', undefined, draft.name);
        option.value = draft.id;
        return option;
      }),
    );
    if (drafts.length === 0) {
      const option = el('option', undefined, 'No saved drafts');
      option.value = '';
      this.draftSelect.append(option);
    } else if (drafts.some((draft) => draft.id === previous)) {
      this.draftSelect.value = previous;
    }
  }

  /** The canvas x for a cell coordinate (fractional cells are fine). */
  private screenX(cellX: number): number {
    return (cellX - this.viewX) * this.scale;
  }

  /** The canvas y for a cell coordinate. */
  private screenZ(cellZ: number): number {
    return (cellZ - this.viewZ) * this.scale;
  }

  private draw(): void {
    const context = this.context;
    if (!context) return;

    context.clearRect(0, 0, MAP_CANVAS_WIDTH, MAP_CANVAS_HEIGHT);
    context.fillStyle = '#05070a';
    context.fillRect(0, 0, MAP_CANVAS_WIDTH, MAP_CANVAS_HEIGHT);

    const left = this.screenX(0);
    const top = this.screenZ(0);
    const right = this.screenX(MAP_GRID_COLS);
    const bottom = this.screenZ(MAP_GRID_ROWS);

    // The board: everything outside it is off-limits and drawn darker.
    context.fillStyle = '#0d1117';
    context.fillRect(left, top, right - left, bottom - top);
    context.strokeStyle = 'rgba(99, 240, 208, 0.45)';
    context.lineWidth = 1.5;
    context.strokeRect(left + 0.5, top + 0.5, right - left - 1, bottom - top - 1);

    // Grid, drawn only over the board so the ruler stops at its edge.
    const firstCol = Math.max(0, Math.floor(this.viewX));
    const lastCol = Math.min(MAP_GRID_COLS, Math.ceil(this.viewX + MAP_CANVAS_WIDTH / this.scale));
    const firstRow = Math.max(0, Math.floor(this.viewZ));
    const lastRow = Math.min(MAP_GRID_ROWS, Math.ceil(this.viewZ + MAP_CANVAS_HEIGHT / this.scale));
    context.lineWidth = 1;
    for (let column = firstCol; column <= lastCol; column += 1) {
      context.strokeStyle =
        column % MAJOR_GRID === 0 ? 'rgba(159, 246, 255, 0.18)' : 'rgba(159, 246, 255, 0.07)';
      const x = this.screenX(column);
      context.beginPath();
      context.moveTo(x, Math.max(top, 0));
      context.lineTo(x, Math.min(bottom, MAP_CANVAS_HEIGHT));
      context.stroke();
    }
    for (let row = firstRow; row <= lastRow; row += 1) {
      context.strokeStyle =
        row % MAJOR_GRID === 0 ? 'rgba(159, 246, 255, 0.18)' : 'rgba(159, 246, 255, 0.07)';
      const z = this.screenZ(row);
      context.beginPath();
      context.moveTo(Math.max(left, 0), z);
      context.lineTo(Math.min(right, MAP_CANVAS_WIDTH), z);
      context.stroke();
    }

    // Everything on the board is clipped to it, so a moved room cannot draw
    // over the ruler or the off-limits area.
    context.save();
    context.beginPath();
    context.rect(left, top, right - left, bottom - top);
    context.clip();

    // Components.
    for (const component of this.map.components) {
      const rect = rectOf(component);
      const x = this.screenX(rect.minX / CELL_SIZE);
      const z = this.screenZ(rect.minZ / CELL_SIZE);
      const w = (rect.width / CELL_SIZE) * this.scale;
      const h = (rect.depth / CELL_SIZE) * this.scale;
      context.fillStyle =
        component.id === this.selectedId ? 'rgba(99, 240, 208, 0.35)' : 'rgba(47, 127, 112, 0.3)';
      context.fillRect(x, z, w, h);
      context.strokeStyle = '#63f0d0';
      context.lineWidth = 1.5;
      context.strokeRect(x + 0.5, z + 0.5, w - 1, h - 1);
      if (this.scale >= 11) {
        context.fillStyle = '#dfe8f2';
        context.font = '10px monospace';
        context.fillText(`${component.kind} ${component.id}`, x + 5, z + 14);
      }
    }

    // Doorways: a highlighted gap on every shared edge.
    context.strokeStyle = '#ffb347';
    context.lineWidth = 4;
    for (const portal of compilePortals(this.map)) {
      const [px, , pz] = portal.position;
      const x = this.screenX(px / CELL_SIZE);
      const z = this.screenZ(pz / CELL_SIZE);
      context.beginPath();
      if (portal.axis === 'x') {
        context.moveTo(x - portal.width * this.scale * 0.5, z);
        context.lineTo(x + portal.width * this.scale * 0.5, z);
      } else {
        context.moveTo(x, z - portal.width * this.scale * 0.5);
        context.lineTo(x, z + portal.width * this.scale * 0.5);
      }
      context.stroke();
    }

    // Placed assets, as small marks in their own colour.
    const assetColors: Record<string, string> = {
      crate: '#c9d6e2',
      console: '#63f0d0',
      pillar: '#8a94a3',
      barrier: '#ffb347',
    };
    for (const asset of this.map.assets) {
      const x = this.screenX(asset.position[0] / CELL_SIZE);
      const z = this.screenZ(asset.position[2] / CELL_SIZE);
      context.fillStyle = assetColors[asset.kind] ?? '#c9d6e2';
      context.fillRect(x - 2, z - 2, 4, 4);
    }

    // Drones and their patrol loops, top-down. The loop is what the 3D builder
    // authored, so the 2D view shows the same monitoring area from above.
    for (const drone of this.map.drones) {
      const route = drone.route ?? [];
      if (route.length >= 2) {
        context.strokeStyle = drone.magnetic
          ? 'rgba(159, 246, 255, 0.75)'
          : 'rgba(99, 240, 208, 0.75)';
        context.lineWidth = 1.5;
        context.setLineDash([4, 3]);
        context.beginPath();
        route.forEach(([rx, , rz], index) => {
          const x = this.screenX(rx / CELL_SIZE);
          const z = this.screenZ(rz / CELL_SIZE);
          if (index === 0) context.moveTo(x, z);
          else context.lineTo(x, z);
        });
        context.closePath();
        context.stroke();
        context.setLineDash([]);
        context.fillStyle = drone.magnetic ? '#9ff6ff' : '#ffb347';
        for (const [rx, , rz] of route) {
          context.fillRect(
            this.screenX(rx / CELL_SIZE) - 1.5,
            this.screenZ(rz / CELL_SIZE) - 1.5,
            3,
            3,
          );
        }
      }
      const x = this.screenX(drone.position[0] / CELL_SIZE);
      const z = this.screenZ(drone.position[2] / CELL_SIZE);
      context.fillStyle = drone.magnetic ? '#9ff6ff' : '#ffe2a8';
      context.beginPath();
      context.moveTo(x, z - 5);
      context.lineTo(x + 4, z + 3);
      context.lineTo(x - 4, z + 3);
      context.closePath();
      context.fill();
    }

    context.restore();

    // The stamp preview under the cursor, turned as the next stamp will be.
    if (this.mode === 'stamp' && this.hoverCell) {
      const [w, d] = footprint(this.kind, this.stampRotation);
      const ok = this.canPlace(this.kind, this.hoverCell, this.stampRotation);
      const x = this.screenX(this.hoverCell[0]);
      const z = this.screenZ(this.hoverCell[1]);
      context.fillStyle = ok ? 'rgba(99, 240, 208, 0.25)' : 'rgba(255, 106, 74, 0.3)';
      context.fillRect(x, z, w * this.scale, d * this.scale);
      context.strokeStyle = ok ? '#63f0d0' : '#ff6a4a';
      context.lineWidth = 1;
      context.strokeRect(x + 0.5, z + 0.5, w * this.scale - 1, d * this.scale - 1);
    }
  }
}

/** Parse a draft, accepting only a real map document. */
function parseDraft(input: unknown): MapDocument | null {
  if (typeof input !== 'object' || input === null) return null;
  const record = input as { version?: unknown; components?: unknown };
  if (record.version !== 1 || !Array.isArray(record.components)) return null;
  return input as MapDocument;
}

/** Hand a JSON string to the browser as a download. */
function download(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Give the click a moment to start before the URL is revoked.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
